import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';
import { kulsoLinkMegnyit } from '../../beadandok/beadando-kozos';

/**
 *  `<app-ora-online [ora]="o">` – a haladási napló egy órájának DIGITÁLIS MUNKAREND blokkja:
 *  az online óra linkje (Teams/Meet/Zoom/Jitsi) és az órához kiadott beadandók.
 *
 *  Szándékosan külön komponens: az óra mentéséhez (téma, hiányzók, könyvelés) nem nyúl – a
 *  link a saját végpontján megy (backend/beadando.js, `ora_online` tábla), azonnal, a napló
 *  Mentés gombjától függetlenül.
 */
@Component({
  selector: 'app-ora-online',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  template: `
    <div class="oo-blokk" [class.oo-digitalis]="!!digitalis" [class.oo-kesz]="!!digitalis && teendoKesz">
      <!-- Digitális munkarendű napon (Nkt. 27. § (2a)) kiemelt felhívás: a teendő a link és/vagy a beadandó. -->
      <div class="oo-felhivas" *ngIf="digitalis" role="status">
        <ion-icon [name]="teendoKesz ? 'checkmark-circle-outline' : 'laptop-outline'" aria-hidden="true"></ion-icon>
        <div>
          <div class="oo-felhivas-cim">Digitális munkarend – add meg az online óra linkjét / adj ki beadandót</div>
          <div class="oo-felhivas-al">
            {{ digitalis.tol }}{{ digitalis.ig && digitalis.ig !== digitalis.tol ? ' – ' + digitalis.ig : '' }}{{ digitalis.ok ? ', ' + digitalis.ok : '' }}:
            az óra online, személyes találkozás nélkül folyik. A jelenlét itt az online részvétel – aki nem vett részt,
            azt a szokásos módon jelöld hiányzónak.
          </div>
          <div class="oo-felhivas-al" *ngIf="betoltve">
            <b>{{ mentett ? 'Online link megadva' : 'Még nincs online link' }}</b> ·
            <b>{{ feladatok.length ? feladatok.length + ' beadandó kiadva' : 'még nincs beadandó' }}</b>
          </div>
        </div>
      </div>
      <div class="oo-sor">
        <ion-item class="oo-link" lines="none">
          <ion-icon slot="start" name="videocam-outline" color="primary" aria-hidden="true"></ion-icon>
          <ion-input label="Online óra link" label-placement="stacked" type="url" inputmode="url"
                     placeholder="https://teams.microsoft.com/… · meet.google.com/… · zoom.us/… · meet.jit.si/…"
                     [(ngModel)]="link" [disabled]="!szerkesztheto || ment" (keyup.enter)="mentes()"></ion-input>
        </ion-item>
        <div class="oo-gombok">
          <ion-button size="small" (click)="mentes()" [disabled]="!szerkesztheto || ment || !valtozott">
            {{ ment ? 'Mentés…' : 'Link mentése' }}
          </ion-button>
          <ion-button size="small" fill="outline" *ngIf="mentett" (click)="megnyit()">
            <ion-icon slot="start" name="open-outline"></ion-icon>Megnyitás
          </ion-button>
          <ion-button size="small" fill="clear" color="medium" *ngIf="mentett && szerkesztheto" (click)="torles()" [disabled]="ment">
            Törlés
          </ion-button>
        </div>
      </div>
      <ion-note color="medium" class="oo-sugo" *ngIf="szerkesztheto">
        A tanulók és a szülők az órarendjükben „Csatlakozás” gombként látják az óra idején.
      </ion-note>
      <ion-note color="danger" class="oo-sugo" *ngIf="hiba">{{ hiba }}</ion-note>

      <div class="oo-beadandok">
        <span class="oo-cim">Beadandó feladat:</span>
        <span *ngIf="!feladatok.length" class="oo-ures">ehhez az órához még nincs.</span>
        <ion-chip *ngFor="let f of feladatok" (click)="megnyitFeladat(f.id)" [outline]="true">
          <ion-icon name="document-attach-outline"></ion-icon>
          <ion-label>{{ f.cim }} · {{ f.beadott_db }}/{{ f.cimzett_db }} beadva · határidő {{ f.hatarido }}</ion-label>
        </ion-chip>
        <ion-button size="small" fill="outline" *ngIf="szerkesztheto" (click)="ujFeladat()">
          <ion-icon slot="start" name="add"></ion-icon>Beadandó kiadása
        </ion-button>
      </div>
    </div>`,
  styles: [`
    .oo-blokk { border: 1px dashed var(--ion-color-medium-tint); border-radius: 8px; padding: 8px 10px; margin: 8px 0; }
    .oo-sor { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
    .oo-link { flex: 1 1 320px; --background: transparent; }
    .oo-gombok { display: flex; flex-wrap: wrap; gap: 4px; }
    .oo-sugo { display: block; font-size: 12px; margin: 2px 0 4px 4px; }
    .oo-beadandok { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 6px; }
    .oo-cim { font-weight: 600; font-size: 13px; }
    .oo-ures { font-size: 13px; color: var(--ion-color-medium); }
    .oo-digitalis { border: 2px solid var(--ion-color-warning); border-style: solid;
                    background: rgba(var(--ion-color-warning-rgb), .08); }
    .oo-digitalis.oo-kesz { border-color: var(--ion-color-success); background: rgba(var(--ion-color-success-rgb), .06); }
    .oo-felhivas { display: flex; gap: 8px; align-items: flex-start; margin-bottom: 6px; }
    .oo-felhivas ion-icon { font-size: 22px; flex: 0 0 auto; color: var(--ion-color-warning-shade); margin-top: 1px; }
    .oo-kesz .oo-felhivas ion-icon { color: var(--ion-color-success); }
    .oo-felhivas-cim { font-weight: 700; font-size: 14px; }
    .oo-felhivas-al { font-size: 12.5px; line-height: 1.4; margin-top: 2px; }
  `],
})
export class OraOnlineComponent implements OnInit {
  /** A napló sora (orarendv2): id kell belőle. */
  @Input() ora: any;
  /**
   *  Ha az óra digitális munkarendű napra esik: az elrendelt időszak (a napló `dmOra(o)`-ja:
   *  { tol, ig, ok }). Ilyenkor a blokk kiemelt felhívással jelenik meg; különben a megszokott alak.
   */
  @Input() digitalis: any = null;

  link = '';
  betoltve = false;
  mentett = '';
  szerkesztheto = false;
  ment = false;
  hiba = '';
  feladatok: any[] = [];

  constructor(private http: HttpClient, private data: DataService, private router: Router) {}

  get valtozott(): boolean { return this.link.trim() !== this.mentett; }

  /** Digitális munkarendben a teendő megvan-e: van mentett online link vagy kiadott beadandó. */
  get teendoKesz(): boolean { return !!this.mentett || this.feladatok.length > 0; }

  async ngOnInit() {
    if (!this.ora?.id) return;
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/beadando/ora/${encodeURIComponent(this.ora.id)}`));
      this.mentett = r?.online?.link || '';
      this.link = this.mentett;
      this.szerkesztheto = !!r?.szerkesztheto;
      this.feladatok = Array.isArray(r?.feladatok) ? r.feladatok : [];
      this.betoltve = true;
    } catch {
      // A blokk nem viheti magával a naplót: ha nem jön, csak üres marad.
    }
  }

  async mentes(ures = false) {
    if (!this.szerkesztheto || this.ment) return;
    this.hiba = '';
    const link = ures ? '' : this.link.trim();
    this.ment = true;
    try {
      const r: any = await firstValueFrom(this.http.put(`/api/beadando/ora/${encodeURIComponent(this.ora.id)}/online`, { link }));
      this.mentett = r?.online?.link || '';
      this.link = this.mentett;
      this.data.sendToast(this.mentett ? 'Az online óra linkje mentve.' : 'Az online óra linkje törölve.');
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A link nem menthető – próbáld újra.';
    } finally {
      this.ment = false;
    }
  }

  torles() { this.mentes(true); }

  megnyit() { kulsoLinkMegnyit(this.mentett); }

  ujFeladat() { this.router.navigate(['/beadandok'], { queryParams: { ora: this.ora.id } }); }

  megnyitFeladat(id: string) { this.router.navigate(['/beadandok'], { queryParams: { feladat: id } }); }
}
